/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { isEmpty } from 'lodash';

import { Typography, Typography2, breakpoints } from '@coursera/cds-core';

import type { EntWebsiteLeader } from 'bundles/ent-website/__generated__/baseContentfulTypes';
import type {
  EntWebsiteBlockAssemblyFragment,
  EntWebsiteBlockLeaderListQuery,
  EntWebsiteLeaderListFragment,
} from 'bundles/ent-website/__generated__/contentfulTypes';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import BlockLayout, { noImage } from 'bundles/ent-website/contentful/core/BlockLayout';
import Portrait from 'bundles/ent-website/contentful/core/Portrait';
import type { Organization, PortraitType } from 'bundles/ent-website/contentful/core/Portrait';
import Description from 'bundles/ent-website/contentful/modules/shared/Description';
import Eyebrow from 'bundles/ent-website/contentful/modules/shared/Eyebrow';
import { entWebsiteBlockLeaderListQuery } from 'bundles/ent-website/queries/EntWebsiteLeaderList';
import parseLayoutOptions from 'bundles/ent-website/utils/parseLayoutOptions';
import type { BlockModuleInnerProps } from 'bundles/ent-website/utils/withBlockQuery';
import { withBlockQuery } from 'bundles/ent-website/utils/withBlockQuery';

type Props = BlockModuleInnerProps<EntWebsiteLeaderListFragment>;

type LeaderProps = {
  portrait: PortraitType;
  organization: Organization;
  layoutOptions?: EntWebsiteBlockAssemblyFragment['layoutOptions'];
} & Pick<EntWebsiteLeader, 'slug' | 'name' | 'leaderTitle'>;

function Leader({ name, leaderTitle, portrait, organization, layoutOptions }: LeaderProps) {
  const options = parseLayoutOptions(layoutOptions);
  const styles = {
    leader: css`
      width: ${options.innerLayout ? 200 : 230}px;
      margin: 0 var(--cds-spacing-200) var(--cds-spacing-600);
    `,
    portrait: css`
      width: 100px;
      height: 100px;
    `,
    name: css`
      margin-top: var(--cds-spacing-200);
      margin-bottom: var(--cds-spacing-200);
      text-align: center;
      color: inherit;
      letter-spacing: -0.02em;
    `,
    leaderTitle: css`
      text-align: center;
      color: inherit;
      letter-spacing: -0.03em;
    `,
  };

  return (
    <div css={styles.leader}>
      <Portrait css={styles.portrait} portrait={portrait} organization={organization} />
      <Typography variant="h2" css={styles.name}>
        {name}
      </Typography>
      <Typography2 component="p" variant="bodySecondary" css={styles.leaderTitle}>
        {leaderTitle}
      </Typography2>
    </div>
  );
}

export function LeaderListModule({ content, assembly }: Props) {
  const styles = {
    leaderContainer: css`
      display: flex;
      margin-top: var(--cds-spacing-1000);
      flex-direction: row;
      width: 100%;
      justify-content: center;
      flex-wrap: wrap;

      ${breakpoints.down('md')} {
        margin-top: var(--cds-spacing-600);
      }
    `,

    headline: css`
      margin-bottom: var(--cds-spacing-400);
      ${breakpoints.down('xs')} {
        margin-bottom: 24px;
      }
    `,

    headlineInlineLayout: css`
      font-size: 20px !important;
      line-height: 150% !important;
      font-weight: 700;
    `,
  };

  function contentPanel() {
    const { eyebrow, visibility } = assembly;
    const options = parseLayoutOptions(assembly.layoutOptions);
    const { headline, description, leadersCollection } = content;
    const leaders = leadersCollection?.items.filter((val) => !isEmpty(val)) ?? [];

    return (
      <>
        <Eyebrow eyebrow={eyebrow} />
        {visibility?.headline && (
          <Typography
            variant="d2semibold"
            className="LeaderListModule-headline"
            css={[styles.headline, options.innerLayout ? styles.headlineInlineLayout : null]}
            color="inherit"
          >
            {headline}
          </Typography>
        )}
        <Description description={description} visible={visibility?.description} module={content} />

        <div css={styles.leaderContainer} className="LeaderListModule-leaderList">
          {leaders.map((leader) => (
            <Leader
              key={leader?.slug}
              name={leader?.name}
              leaderTitle={leader?.leaderTitle}
              portrait={leader?.portrait}
              organization={leader?.organization}
              layoutOptions={assembly.layoutOptions}
            />
          ))}
        </div>
      </>
    );
  }

  return <BlockLayout assembly={noImage(assembly)} renderContent={contentPanel} />;
}

// @ts-expect-error TSMIGRATION-5.0 TS2344
export const BlockLeaderListModule = withBlockQuery<EntWebsiteLeaderListFragment, EntWebsiteBlockLeaderListQuery>(
  entWebsiteBlockLeaderListQuery
)(LeaderListModule);
